import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLicence } from '../context/LicenceContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import BrandMark from './BrandMark';
import DevMenu from './DevMenu';
import UserMenu from './UserMenu';
import { takeFlash } from '../lib/signInMethods';

// shadcn UI components
import { Button } from './ui/button';
import { Progress } from './ui/progress';

// Import SVG icons as React components so they inherit currentColor
import { ReactComponent as DashboardIcon } from '../assets/Icons/dashboard.svg';
import { ReactComponent as QuoteRequestsIcon } from '../assets/Icons/quote-requests.svg';
import { ReactComponent as PhonebookIcon } from '../assets/Icons/phonebook.svg';
import { ReactComponent as FleetIcon } from '../assets/Icons/fleet.svg';
// Hidden for the MVP — see menuItems below.
// import { ReactComponent as JobsIcon } from '../assets/Icons/jobs.svg';
// import { ReactComponent as ResourcesIcon } from '../assets/Icons/resources.svg';
// import { ReactComponent as InventoryIcon } from '../assets/Icons/inventory.svg';
// import { ReactComponent as ReportsIcon } from '../assets/Icons/reports.svg';
import { ReactComponent as SettingsIcon } from '../assets/Icons/settings.svg';
import { ReactComponent as SupportIcon } from '../assets/Icons/support.svg';

const Layout = ({ children }) => {
  const { user } = useAuth();
  const { usage, planName, isFreePlan } = useLicence();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // A result to report from a redirect that just brought the user back here,
  // such as connecting a sign-in provider. See AuthCallback.
  useEffect(() => {
    const flash = takeFlash();
    if (!flash) return;
    if (flash.type === 'error') toast.error(flash.message);
    else toast.success(flash.message);
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  /**
   * Plan meter in the sidebar footer.
   *
   * Only for shops on the free plan, and deliberately quiet: a line of text and
   * a thin bar. It exists so the allowance is never a surprise — someone should
   * find out they're near the end from here, in their own time, rather than
   * from a blocked button halfway through quoting a car. It becomes a prompt
   * only once there's genuinely nothing left.
   */
  const PlanMeter = () => {
    if (!isFreePlan || !usage || usage.unlimited) return null;

    const pct = usage.limit > 0 ? (usage.used / usage.limit) * 100 : 100;

    return (
      <Link to="/settings/general" className="block mb-4 group">
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
              {planName || 'Free'} plan
            </span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {usage.used}/{usage.limit}
            </span>
          </div>
          <Progress
            value={pct}
            className={`h-1 ${usage.exhausted ? '[&>div]:bg-amber-500' : ''}`}
          />
          <p className="text-xs text-muted-foreground">
            {usage.exhausted
              ? 'AI assessments used up — see plans'
              : 'AI assessments this month'}
          </p>
        </div>
      </Link>
    );
  };

  // Jobs, Resources, Inventory and Reports are commented out for the MVP: they
  // route to screens that are not ready to hand to a shop. Uncomment an entry
  // and its icon import above to bring one back. Their routes are commented out
  // in App.js too — hiding the link alone leaves the page reachable by typing
  // the URL.
  const menuItems = [
    {
      path: '/dashboard',
      name: 'Dashboard',
      icon: DashboardIcon
    },
    {
      path: '/quote-requests',
      name: 'Quotes',
      icon: QuoteRequestsIcon
    },
    // {
    //   path: '/jobs',
    //   name: 'Jobs',
    //   icon: JobsIcon
    // },
    {
      path: '/phonebook',
      name: 'Phonebook',
      icon: PhonebookIcon
    },
    // {
    //   path: '/resources',
    //   name: 'Resources',
    //   icon: ResourcesIcon
    // },
    // {
    //   path: '/inventory',
    //   name: 'Inventory',
    //   icon: InventoryIcon
    // },
    {
      path: '/fleet',
      name: 'Fleet',
      icon: FleetIcon
    },
    // {
    //   path: '/reports',
    //   name: 'Reports',
    //   icon: ReportsIcon
    // },
    {
      path: '/settings',
      name: 'Settings',
      icon: SettingsIcon
    },
    {
      path: '/support',
      name: 'Support',
      icon: SupportIcon
    }
  ];

  const isActivePath = (path) => {
    if (path === '/dashboard' && location.pathname === '/') return true;
    return location.pathname === path;
  };

  // Called, not mounted as <SidebarContent />: a component defined in here is a
  // new type on every render, so React would remount the sidebar each time
  // Layout re-renders (a theme change re-renders the whole app) and close the
  // user menu mid-use.
  const renderSidebarContent = () => (
    <div className="flex flex-col h-full bg-sidebar">
      {/* Sidebar Header */}
      <div className={`${sidebarCollapsed ? 'px-2' : 'px-4'} py-6`}>
        <div className={`flex items-center min-w-0 ${sidebarCollapsed ? 'justify-center' : ''}`}>
          <BrandMark
            showText={!sidebarCollapsed}
            size={sidebarCollapsed ? 'sm' : 'md'}
            className="min-w-0"
          />
        </div>
      </div>

      {/* Sidebar Navigation */}
      <div className="flex-1 p-4">
        <nav className="space-y-2">
          {menuItems.map((item) => (
            <Link key={item.path} to={item.path}>
              <Button
                variant={isActivePath(item.path) ? "secondary" : "ghost"}
                className={`w-full h-11 gap-3 ${
                  sidebarCollapsed ? 'justify-center px-2' : 'justify-start px-4'
                }`}
                title={sidebarCollapsed ? item.name : ''}
              >
                <item.icon
                  aria-hidden="true"
                  className="h-5 w-5 min-h-[20px] min-w-[20px] shrink-0"
                />
                {!sidebarCollapsed && (
                  <span className="truncate">{item.name}</span>
                )}
              </Button>
            </Link>
          ))}
        </nav>
      </div>

      {/* Sidebar Footer - User Section. Collapsed, only the user menu stays. */}
      <div className={sidebarCollapsed ? 'px-4 pt-4' : 'p-4'}>
        {!sidebarCollapsed && (
          <>
            <div className="mb-4">
              <p className="text-sm text-muted-foreground truncate">
                {user?.organisation?.name || user?.organisationName}
              </p>
            </div>

            <PlanMeter />
          </>
        )}

        <UserMenu collapsed={sidebarCollapsed} />
      </div>

      {/* Footer: copyright and collapse toggle */}
      <div className={`p-4 flex items-center gap-3 ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
        {!sidebarCollapsed && (
          <p className="text-xs text-muted-foreground">
            &copy; 2026 PanelGear
          </p>
        )}
        {!sidebarCollapsed && <div className="flex-1" />}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="h-8 w-8 p-0"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <div className={`hidden md:flex flex-col transition-all duration-300 ${
        sidebarCollapsed ? 'w-[75px]' : 'w-64'
      }`}>
        {renderSidebarContent()}
      </div>

      {/* Mobile Sidebar */}
      <div className="md:hidden">
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="fixed left-0 top-0 h-full w-64 bg-sidebar">
              {renderSidebarContent()}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between p-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(true)}
            className="h-8 w-8 p-0"
          >
            ☰
          </Button>
          <div className="flex-1 flex justify-center">
            <BrandMark size="md" />
          </div>
          <div className="w-8" /> {/* Spacer for centering */}
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* Floating dev tools (only visible when dev mode is enabled) */}
      <DevMenu />
    </div>
  );
};

export default Layout;